import type {
  Product,
  ProductListConfig,
  SocialProofMessage,
  OnlineStockData,
} from "../types/types";
import fetchSocialProofData, {
  getSocialProofAppIdFromConfig,
  shouldFetchSocialProof,
} from "./fetchSocialProofData";
import { useRef } from "react";
import {
  fetchOnlineStockDataForProducts,
  shouldFetchOnlineStock,
} from "./fetchOnlineStockData";

/**
 * Custom hook to enrich a list of products with social proof and online stock data.
 *
 * Returns products synchronously, each with `socialProofMessages` and
 * `initialVariantOnlineStock` as promises. Consumers should unwrap these with
 * React.use() inside Suspense boundaries.
 *
 * Enrichment requests are batched and cached by product position and identity.
 * Appended products (e.g. via chunked loading) only trigger requests for the
 * new entries — existing cached promises are reused. On the server, enrichment
 * is skipped and products are returned with resolved empty promises.
 */
export const useEnrichedProducts = (
  products: Product[],
  config: ProductListConfig,
): Product[] => {
  const cacheRef = useRef<Map<string, EnrichmentCacheEntry>>(new Map());

  // Enrichment fetches are client-only. On the server, return products with
  // resolved empty promises so React.use() never suspends during SSR.
  if (typeof window === "undefined") {
    return products.map((product) => ({
      ...product,
      socialProofMessages: Promise.resolve(undefined),
      initialVariantOnlineStock: Promise.resolve(undefined),
    }));
  }

  const indexed: IndexedProduct[] = products.map((product, i) => ({
    product,
    key: productKey(product, i),
  }));

  // Evict cache entries for products no longer in the list.
  // This implicitly handles both fresh product lists (all entries evicted)
  // and appended lists (only stale entries evicted).
  const currentKeys = new Set(indexed.map(({ key }) => key));
  cacheRef.current = new Map(
    [...cacheRef.current].filter(([key]) => currentKeys.has(key)),
  );

  // Batch fetch enrichment data for all products not yet in the cache
  const uncached = indexed.filter(({ key }) => !cacheRef.current.has(key));

  if (uncached.length > 0) {
    const productList = uncached.map(({ product }) => product);

    const socialProofBatch = (
      shouldFetchSocialProof(config)
        ? fetchSocialProofData(
            productList,
            getSocialProofAppIdFromConfig(config),
          )
        : Promise.resolve([])
    ).catch(() => []);

    const onlineStockBatch = (
      shouldFetchOnlineStock(config)
        ? fetchOnlineStockDataForProducts(productList)
        : Promise.resolve([])
    ).catch(() => []);

    const newEntries: [string, EnrichmentCacheEntry][] = uncached.map(
      ({ product, key }) => [
        key,
        {
          socialProofMessages: socialProofBatch.then(
            (data) =>
              data.find((sp) => sp.productId === product.aliases.pimProductId)
                ?.messages,
          ),
          initialVariantOnlineStock: onlineStockBatch.then((data) =>
            data.find((os) =>
              product.variants.map((v) => v.aliases.skuId).includes(os.skuId),
            ),
          ),
        },
      ],
    );

    cacheRef.current = new Map([...cacheRef.current, ...newEntries]);
  }

  return indexed.map(({ product, key }) => {
    const entry = cacheRef.current.get(key)!;
    return {
      ...product,
      socialProofMessages: entry.socialProofMessages,
      initialVariantOnlineStock: entry.initialVariantOnlineStock,
    };
  });
};

type IndexedProduct = { product: Product; key: string };

type EnrichmentCacheEntry = {
  socialProofMessages: Promise<SocialProofMessage[] | undefined>;
  initialVariantOnlineStock: Promise<OnlineStockData | undefined>;
};

/**
 * Builds a key that uniquely identifies a product instance within a list.
 * Index is included because the same product (or even the same product with
 * the same selected SKU) can appear more than once, so product-level IDs
 * alone cannot guarantee uniqueness.
 */
const productKey = (product: Product, index: number): string =>
  `${index}:${product.aliases.pimProductId}`;
